import { useCallback, useEffect, useRef, useState } from "react";
import { useSocket } from "../../contexts/SocketContext";
import { fetchPaymentsV2Query } from "../models/paymentsV2Model";

const eventQueryPks = payload => {
  const data = payload?.data ?? payload ?? {};
  const values = data.query_pks ?? [data.query_pk ?? data.query?.id ?? data.task?.id ?? data.id];
  return new Set((Array.isArray(values) ? values : [values]).filter(Boolean).map(String));
};

export function usePaymentsV2Query(queryPk, { enabled = true } = {}) {
  const { socket } = useSocket();
  const [projection, setProjection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestRef = useRef(0);
  const timerRef = useRef(null);

  const reload = useCallback(async ({ silent = false } = {}) => {
    if (!enabled || !queryPk) return null;
    const request = ++requestRef.current;
    if (!silent) setLoading(true);
    try {
      const result = await fetchPaymentsV2Query(queryPk);
      if (request === requestRef.current) {
        setProjection(result);
        setError(null);
      }
      return result;
    } catch (nextError) {
      if (request === requestRef.current) setError(nextError);
      throw nextError;
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [enabled, queryPk]);

  useEffect(() => {
    requestRef.current += 1;
    setProjection(null);
    setError(null);
    if (enabled && queryPk) reload().catch(() => {});
    return () => { requestRef.current += 1; };
  }, [enabled, queryPk, reload]);

  useEffect(() => {
    if (!socket || !enabled || !queryPk) return undefined;
    const schedule = payload => {
      if (!eventQueryPks(payload).has(String(queryPk))) return;
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => reload({ silent: true }).catch(() => {}), 180);
    };
    const reconnect = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => reload({ silent: true }).catch(() => {}), 80);
    };
    socket.on("payments:v2_updated", schedule);
    socket.on("query:updated", schedule);
    socket.on("query:task_moved", schedule);
    socket.on("connect", reconnect);
    return () => {
      clearTimeout(timerRef.current);
      socket.off("payments:v2_updated", schedule);
      socket.off("query:updated", schedule);
      socket.off("query:task_moved", schedule);
      socket.off("connect", reconnect);
    };
  }, [socket, enabled, queryPk, reload]);

  return { projection, setProjection, loading, error, reload,
    capabilities: projection?.capabilities || {}, summary: projection?.summary || null,
    timeline: projection?.timeline || [], revisions: projection?.invoice_revisions || [],
    capability: key => projection?.capabilities?.[key] || { allowed: false, reason_code: null, reason: 'Not available' } };
}
